import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { QuestionnaireService, QuestionnaireMetrics } from 'src/app/services/questionnaire/questionnaire.service';
import { faCaretUp, faCaretDown, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { BuilderService } from 'src/app/services/builder/builder.service';
import { EditModeService } from 'src/app/services/edit-mode/edit-mode.service';
import { SearchService } from 'src/app/services/search/search.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { I18nHelperService, AlertListService } from '@xref/ngx';
import { Subject } from 'rxjs';

@Component({
    selector: 'xqnr-builder',
    templateUrl: './builder.component.pug',
    styleUrls: ['./builder.component.scss'],
    standalone: false
})
export class BuilderComponent implements OnInit {
  @ViewChild('contentMain') contentMain: ElementRef;

  loaded = false;

  questionnaireTitleField = '';

  fa: any = {
    caretUp: faCaretUp,
    caretDown: faCaretDown,
    circleNotch: faCircleNotch
  };

  questionnaireTouched = false;

  stats: QuestionnaireMetrics = {
    essential: 0,
    person: 0,
    role: 0
  };

  searchInput = new FormControl('');
  searchResults: any[] = [];

  showSuggestions = false;

  sentToEmail$ = new Subject();

  constructor(
    private qs: QuestionnaireService,
    private router: Router,
    private suggestions: SuggestionService,
    private builder: BuilderService,
    public editMode: EditModeService,
    private search: SearchService,
    private auth: AuthService,
    public route: ActivatedRoute,
    public i18nHelper: I18nHelperService,
    private alertList: AlertListService
  ) {}

  ngOnInit() {
    this.route.data.subscribe((data: any) => {
      this.getQuestionnaire(data.questionnaire.id);
    });

    this.setInitEditMode();

    this.builder.questionAdded$.subscribe(this.handleQuestionAdded);
  }

  setLoaded(value: boolean) {
    this.loaded = value;
  }

  // If session is already verified activate edit mode
  setInitEditMode() {
    if (this.auth.isLoggedIn())
      this.editMode.setEditMode(true);
    else
      this.editMode.setEditMode(false);
  }

  getQuestionnaireItemsSuccess = (data: any) => {
    this.builder.addQuestionnaire(data);
    this.updateQuestionnaireStats();
    this.suggestions.setSuggestions();
    this.setLoaded(true);
  };

  getQuestionnaireItems(id: string) {
    this.qs.getQuestionnaireItems(id).subscribe(
      this.getQuestionnaireItemsSuccess,
      this.handleApiError
    );
  }

  getQuestionnaireSuccess = (res: any) => {
    if (res.cloned) {
      this.router.navigate([`/build/${res.id}`]);

    } else {
      const qData = {
        id: res.id,
        title: res.title,
        questions: []
      };

      // Pre-fill local title field with existing title value
      this.questionnaireTitleField = res.title;

      this.builder.questionnaire = qData;
      this.getQuestionnaireItems(res.id);
    }
  };

  getQuestionnaire(id: string) {
    this.qs.getQuestionnaire(id)
      .subscribe(this.getQuestionnaireSuccess, this.handleApiError);
  }

  updateQuestionnaireStats() {
    this.stats = this.qs.getCategoryMetrics(this.builder.questionnaire);
  }

  // User dragged question to new position
  questionReorderedByDrag(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.builder.questionnaire.questions, event.previousIndex, event.currentIndex);
    this.reorderQuestionnaire();
  }

  // "Arrow up" button clicked
  orderQuestionUp(index: number) {
    if (index > 0) {
      moveItemInArray(this.builder.questionnaire.questions, index, index - 1);
      this.reorderQuestionnaire();
    }
  }

  // "Arrow down" button clicked
  orderQuestionDown(index: number) {
    if (index < this.builder.questionnaire.questions.length) {
      moveItemInArray(this.builder.questionnaire.questions, index, index + 1);
      this.reorderQuestionnaire();
    }
  }

  handleQuestionAdded = () => {
    // Scroll to bottom, timeout ensures the DOM re-renders first
    setTimeout(() => window.scroll(0, this.contentMain.nativeElement.clientHeight), 0);

    this.updateQuestionnaireStats();
    this.suggestions.setSuggestions();
    this.markQuestionnaireTouched();
  };

  questionDeleted(index: number) {
    this.builder.questionnaire.questions.splice(index, 1);
    this.updateQuestionnaireStats();
    this.suggestions.setSuggestions();
    this.markQuestionnaireTouched();
  }

  reorderQuestionnaireSuccess = () => {
    // Do nothing
  };

  reorderQuestionnaire() {
    this.qs.reorderQuestionnaire(this.builder.questionnaire)
      .subscribe(
        this.reorderQuestionnaireSuccess,
        this.handleApiError
      );
  }

  markQuestionnaireTouched() {
    this.questionnaireTouched = true;
  }

  handleCompetencyClick(competency: string) {
    event.preventDefault();
    this.search.searchFor(competency);
  }

  updateQTitleSuccess = () => {
    // Do nothing
  };

  updateQTitleError = (err: any) => {
    if (err.status == 400)
      this.alertList.append([{active: true, type: 'danger', messageKey: 'builder.questionnaireTitle.error', timeout: 3000}]);
    else
      this.handleApiError(err);
  };

  updateQTitle(title: string) {
    this.questionnaireTitleField = title;

    this.qs.editTitle(this.builder.questionnaire.id, title)
      .subscribe(this.updateQTitleSuccess, this.updateQTitleError);
  }

  handleApiError = (err: any) => {
    console.error(err);

    if (err.status == 401) {
      this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.expiredSession', timeout: 60000}]);
    } else {
      this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
    }
  };
}
